<template>
  <BaseModal
    :modalTitle="order ? `#${order.orderNumber}` : ''"
    contentPadding="none">
    <div
      v-if="!order"
      class="px-6 pb-6 text-sm text-text-secondary">
      {{ $t('error.common.standard') }}
    </div>

    <div
      v-else-if="remainingSamples.length === 0"
      class="px-6 pb-6 text-sm text-text-secondary">
      {{ $t('W: Alle prøver på denne ordre er modtaget') }}
    </div>

    <template v-else>
      <div class="px-6 pb-6">
        <div class="overflow-hidden rounded-lg border border-border-subtle">
          <!-- Select all row -->
          <label
            class="bg-surface-muted flex cursor-pointer items-center justify-between gap-3 px-4 py-2.5">
            <span class="flex items-center gap-3">
              <input
                type="checkbox"
                class="accent-primary h-4 w-4 cursor-pointer"
                :checked="isAllSelected"
                @change="onToggleSelectAll" />
              <span class="text-xs font-semibold tracking-wider uppercase">
                {{ $t('W: Vælg alle') }}
              </span>
            </span>
            <span class="text-xs text-text-secondary">
              {{ $t('W: {count} valgt', { count: selectedSampleIDs.length }) }}
            </span>
          </label>

          <!-- Sample rows -->
          <div class="max-h-88 overflow-y-auto">
            <label
              v-for="sample in order.samples"
              :key="sample.sampleID"
              class="flex items-center gap-3 border-t border-border-subtle px-4 py-2.5"
              :class="
                sample.status === 'not-registered'
                  ? 'hover:bg-surface-muted cursor-pointer'
                  : 'cursor-not-allowed'
              ">
              <input
                type="checkbox"
                class="accent-primary h-4 w-4 shrink-0 cursor-pointer disabled:cursor-not-allowed"
                :disabled="sample.status !== 'not-registered'"
                :checked="selectedSampleIDs.includes(sample.sampleID)"
                @change="onToggleSample(sample.sampleID)" />
              <div class="flex min-w-0 flex-1 flex-col gap-0.5">
                <div class="flex items-center justify-between gap-2">
                  <span class="flex items-baseline gap-2">
                    <span class="text-sm font-semibold text-text-primary">
                      {{ getSampleLabel(sample.sampleNumber, currentLocale) }}
                    </span>
                    <span class="text-xs text-text-secondary">
                      {{ sample.label.id }}
                    </span>
                  </span>
                  <UiStatusChip
                    :variant="getSampleStatusIndicator(sample).variant"
                    :label="getSampleStatusIndicator(sample).label" />
                </div>
                <span class="text-sm text-text-secondary">
                  {{ getAnalysesText(sample) }}
                </span>
                <span
                  v-if="sample.forwarding"
                  class="text-xs text-text-secondary">
                  {{
                    $t('W: Videresendt til {name}', {
                      name: getForwardedToName(sample),
                    })
                  }}
                </span>
              </div>
            </label>
          </div>
        </div>
      </div>

      <!-- Sticky footer -->
      <div class="flex justify-end gap-2 border-t border-border-subtle px-6 py-3">
        <UiBaseButton
          v-if="hasUnforwardableSelection && canUser('orders.registerSamples')"
          theme="neutral"
          :text="$t('W: Fjern videresendelse')"
          :isLoading="isSaving"
          :showSaveIcon="showSaveIcon"
          @button-click="onUnforwardClick" />
        <UiBaseButton
          v-if="canForward && canUser('orders.registerSamples')"
          theme="neutral"
          :text="$t('W: Videresend')"
          :isDisabled="!hasSelection"
          :isLoading="isSaving"
          @button-click="onForwardClick" />
        <UiBaseButton
          v-if="canUser('orders.registerSamples')"
          :text="$t('W: Marker som modtaget')"
          :isDisabled="!hasSelection"
          :isLoading="isSaving"
          :showSaveIcon="showSaveIcon"
          @button-click="onMarkAsReceivedClick" />
      </div>
    </template>
  </BaseModal>
</template>

<script lang="ts">
import { getSampleLabel } from '@/globals/typescript/utils/sampleLabel'
import { getSampleStatusIndicator } from '@/globals/typescript/utils/orderSampleStatus'
import { useToast } from '@/globals/typescript/utils/toast'
import { useAnalysisStore } from '@/stores/analysisStore'
import { useAppStore } from '@/stores/appStore'
import { useLabLocationStore } from '@/stores/labLocationStore'
import { useModalStore } from '@/stores/modalStore'
import { useOrderStore } from '@/stores/orderStore'
import { useUserStore } from '@/stores/userStore'
import type { IOrderSample } from '@team-milva/lab-models/resources'
import { mapActions, mapState } from 'pinia'
import type { PropType } from 'vue'

export default {
  name: 'ReceiveOrderSamplesModal',

  emits: ['close'],

  props: {
    orderID: { type: String as PropType<string>, required: true },
  },

  data() {
    return {
      selectedSampleIDs: [] as string[],
      isSaving: false,
      showSaveIcon: false,
    }
  },

  computed: {
    ...mapState(useOrderStore, ['allOrders']),
    ...mapState(useAppStore, ['currentLocale', 'defaultMessageSaveIconTime']),
    ...mapState(useUserStore, ['currentUser']),
    ...mapState(useLabLocationStore, ['activeLabLocations', 'labLocations']),
    ...mapState(useAnalysisStore, ['allAnalyses']),

    order() {
      return this.allOrders.find((x) => x.id === this.orderID)
    },

    remainingSamples() {
      if (!this.order) return []
      return this.order.samples.filter((x) => x.status === 'not-registered')
    },

    isAllSelected(): boolean {
      return (
        this.remainingSamples.length > 0 &&
        this.remainingSamples.every((x) =>
          this.selectedSampleIDs.includes(x.sampleID)
        )
      )
    },

    selectedSamples() {
      if (!this.order) return []
      return this.order.samples.filter((x) =>
        this.selectedSampleIDs.includes(x.sampleID)
      )
    },

    // Mark as Received / Forward are only disabled when the selection is empty —
    // never based on the eligibility composition of what's selected (mixed
    // selections are fine; the action just silently no-ops on any ineligible
    // samples in the selection).
    hasSelection(): boolean {
      return this.selectedSampleIDs.length > 0
    },

    // Unforward is the exception: it's only shown at all when the selection
    // actually contains a forwarded sample — otherwise there's nothing for it to do.
    hasUnforwardableSelection(): boolean {
      return this.selectedSamples.some((x) => x.forwarding !== null)
    },

    // Mirrors the backend's checkForwardEligibility: the lab-wide gate (≥2 active
    // locations — implies ≥1 of them is canForwardSamples-eligible, since the
    // caller's own location below must be one of them) plus the caller's own
    // location eligibility. The Forward button is hidden (not just disabled)
    // when this is false.
    canForward(): boolean {
      if (this.activeLabLocations.length < 2) return false
      const assignedLocationID = this.currentUser?.assignedLocationID
      if (!assignedLocationID) return false
      const location = this.activeLabLocations.find(
        (x) => x.id === assignedLocationID
      )
      return !!location?.options.canForwardSamples
    },
  },

  methods: {
    ...mapActions(useModalStore, ['open']),
    ...mapActions(useUserStore, ['canUser']),
    ...mapActions(useOrderStore, [
      'registerSamples',
      'unforwardSamples',
    ]),

    getSampleLabel,
    getSampleStatusIndicator,

    getAnalysesText(sample: IOrderSample): string {
      return sample.analyses
        .filter((x) => x.status !== 'cancelled')
        .map(
          (x) =>
            this.allAnalyses.find((a) => a.id === x.analysisID)?.getName(
              this.currentLocale
            ) ?? x.analysisID
        )
        .join(', ')
    },

    getForwardedToName(sample: IOrderSample): string {
      if (!sample.forwarding) return ''
      return (
        this.labLocations.find((x) => x.id === sample.forwarding?.toLocationID)
          ?.name ?? '—'
      )
    },

    onToggleSelectAll(): void {
      if (this.isAllSelected) {
        this.selectedSampleIDs = []
        return
      }
      this.selectedSampleIDs = this.remainingSamples.map((x) => x.sampleID)
    },

    onToggleSample(sampleID: string): void {
      const idx = this.selectedSampleIDs.indexOf(sampleID)
      if (idx === -1) {
        this.selectedSampleIDs.push(sampleID)
      } else {
        this.selectedSampleIDs.splice(idx, 1)
      }
    },

    async onMarkAsReceivedClick(): Promise<void> {
      if (!this.order) return
      const eligibleIDs = this.selectedSamples
        .filter((x) => x.status === 'not-registered')
        .map((x) => x.sampleID)
      if (!eligibleIDs.length) return

      this.isSaving = true
      try {
        await this.registerSamples(this.orderID, eligibleIDs)
        this.showSaveIcon = true
        setTimeout(() => {
          this.showSaveIcon = false
        }, this.defaultMessageSaveIconTime)
        useToast().success(this.$t('W: Prøver modtaget'))
        this.selectedSampleIDs = []
      } catch {
        useToast().error(this.$t('W: Kunne ikke modtage prøver'))
      } finally {
        this.isSaving = false
      }
    },

    onForwardClick(): void {
      const eligibleIDs = this.selectedSamples
        .filter((x) => x.status === 'not-registered')
        .map((x) => x.sampleID)
      if (!eligibleIDs.length) return

      this.open({
        name: 'ForwardSamplesModal',
        width: 'sm',
        props: { orderID: this.orderID, sampleIDs: eligibleIDs },
      })
    },

    onUnforwardClick(): void {
      const eligibleIDs = this.selectedSamples
        .filter((x) => x.forwarding !== null)
        .map((x) => x.sampleID)
      if (!eligibleIDs.length) return

      this.open({
        name: 'ConfirmationModal',
        width: 'sm',
        props: {
          title: this.$t('W: Fjern videresendelse'),
          textLines: [
            this.$t('W: Er du sikker på, at du vil fjerne videresendelsen for de valgte prøver?'),
          ],
          onConfirm: async () => {
            this.isSaving = true
            try {
              await this.unforwardSamples(this.orderID, eligibleIDs)
              this.showSaveIcon = true
              setTimeout(() => {
                this.showSaveIcon = false
              }, this.defaultMessageSaveIconTime)
              useToast().success(this.$t('W: Videresendelse fjernet'))
              this.selectedSampleIDs = []
            } catch {
              useToast().error(this.$t('W: Kunne ikke fjerne videresendelse'))
            } finally {
              this.isSaving = false
            }
          },
        },
      })
    },
  },
}
</script>
